import React from "react";
import { usePropertyPhotosQuery } from "../../../hooks/valuation/usePropertyPhotosQuery";
import { useTenant } from "../../../contexts/TenantContext";
import { PropertyValuationData } from "../../../types/valuation";

interface CompGalleryProps {
  comp: NonNullable<PropertyValuationData["comps"]>[0];
  className?: string;
}

export const CompGallery: React.FC<CompGalleryProps> = ({
  comp,
  className = "",
}) => {
  const { config } = useTenant();
  const colors = config?.branding?.colors || {
    primary: "#000000",
    secondary: "#000000",
    accent: "#000000",
    background: "#ffffff",
    text: "#000000",
  };

  // Transform comp data to match PropertyValuationData structure
  const propertyData: Omit<PropertyValuationData, "id"> & {
    id: string | number;
  } = {
    id: comp.id || `comp-${Date.now()}`,
    estimatedValue: comp.estimatedValue || comp.mlsSoldPrice || 0,
    propertyInfo: {
      photos: [],
      address: comp.address
        ? {
            address: comp.address.address,
            city: comp.address.city || "Unknown",
            state: comp.address.state || "Unknown",
            zip: comp.address.zip || "Unknown",
          }
        : undefined,
      yearBuilt: comp.yearBuilt,
      bedrooms: comp.bedrooms,
      bathrooms: comp.bathrooms,
      livingSquareFeet: comp.squareFeet,
      latitude: comp.latitude || undefined,
      longitude: comp.longitude || undefined,
    },
    propertyType: "comp",
  };

  const { mainPhotos, isLoading: loading } =
    usePropertyPhotosQuery(propertyData);

  if (loading) {
    return (
      <div
        className={`${className} h-48 rounded-t-lg animate-pulse`}
        style={{ backgroundColor: `${colors.background}10` }}
      />
    );
  }

  if (!mainPhotos) {
    return (
      <div
        className={`${className} h-48 rounded-t-lg flex items-center justify-center`}
        style={{ backgroundColor: `${colors.background}10` }}
      >
        <svg
          className="h-16 w-16"
          fill="none"
          stroke={colors.text}
          style={{ opacity: 0.4 }}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className={`${className} relative h-48 rounded-t-lg overflow-hidden`}>
      <img
        src={mainPhotos.primaryUrl}
        alt={`Comparable property${
          comp.address?.address ? ` at ${comp.address.address}` : ""
        }`}
        className="w-full h-full object-cover"
        onError={(e) => {
          console.error("Failed to load comp photo:", mainPhotos.primaryUrl);
          e.currentTarget.src = "/assets/images/place-holder.webp";
        }}
      />

      {/* Photo source indicator */}
      {mainPhotos.type !== "placeholder" && (
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
          {(() => {
            switch (mainPhotos.type) {
              case "valuation":
                return mainPhotos.photoCount
                  ? `Photo ${mainPhotos.photoCount}`
                  : "Property Photo";
              case "streetview":
                return "Street View";
              default:
                return null;
            }
          })()}
        </div>
      )}
    </div>
  );
};

export default CompGallery;
